import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";

import { useMeeting, usePubSub } from "../../config/sdkConfig";

import { useSiteContext } from "../../context/SiteContext";
import pollServices from "../../services/pollServices";

const PollVotingModal = ({ poll, isOpen, onClose, onVoteSubmitted, onEndPoll, isModeratorView = false }) => {
  const mMeeting = useMeeting();
  const { user, meetingSession, meetingSettings } = useSiteContext();
  const { publish: publishPollVote } = usePubSub("POLL_VOTE");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [voteResults, setVoteResults] = useState(null);
  const [openTextResponse, setOpenTextResponse] = useState("");
  const [countdown, setCountdown] = useState(null);
  const countdownRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // Listen for poll results updates from moderator
  usePubSub("POLL_VOTE", {
    onMessageReceived: (data) => {
      const messageData = data.message || data;
      const { type, poll: updatedPoll } = messageData;

      // Update results if this is a results update for the current poll
      if (
        type === "results_update" &&
        updatedPoll &&
        updatedPoll.id === poll?.id
      ) {
        setVoteResults(updatedPoll);
      }
    },
  });

  // Animate in after mount
  useEffect(() => {
    if (isOpen) {
      // Use rAF to ensure the initial state renders first, then animate in
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  // Reset state when poll changes
  useEffect(() => {
    if (poll) {
      setSelectedOptions([]);
      setHasVoted(false);
      setVoteResults(null);
      setOpenTextResponse("");
    }
  }, [poll?.id]);

  // Auto-close timer with visual countdown
  useEffect(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    if (
      poll?.settings?.autoCloseAfter &&
      poll?.settings?.autoCloseSeconds &&
      isOpen &&
      !hasVoted
    ) {
      const seconds = poll.settings.autoCloseSeconds;
      setCountdown(seconds);

      countdownRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
            handleClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
        }
      };
    } else {
      setCountdown(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    poll?.settings?.autoCloseAfter,
    poll?.settings?.autoCloseSeconds,
    isOpen,
    hasVoted,
  ]);

  const handleOptionToggle = (optionIndex) => {
    if (!poll || hasVoted) return;

    if (poll.settings?.allowMultipleAnswers) {
      // Multiple selection
      setSelectedOptions((prev) => {
        if (prev.includes(optionIndex)) {
          return prev.filter((idx) => idx !== optionIndex);
        } else {
          return [...prev, optionIndex];
        }
      });
    } else {
      // Single selection
      setSelectedOptions([optionIndex]);
    }
  };

  const handleSkipVote = async () => {
    if (!poll || hasVoted) {
      handleClose();
      return;
    }

    const participantId = mMeeting?.localParticipant?.id;
    const participantName =
      mMeeting?.localParticipant?.displayName || user?.name || meetingSettings?.yourName || "Guest";

    if (!participantId) {
      toast.error("Unable to skip poll. Participant ID not found.");
      return;
    }

    setIsSubmitting(true);

    const skipData = {
      sessionId: meetingSession || mMeeting?.meetingId,
      meetingId: mMeeting?.meetingId,
      pollId: poll.id,
      participantId,
      participantName: participantName,
      timestamp: Date.now(),
    };

    try {
      await publishPollVote({
        type: "vote_skipped",
        pollId: poll.id,
        skip: skipData,
      });

      // Persist skip to database via API (submit with empty optionIndices)
      try {
        await pollServices.submitVote({
          ...skipData,
          optionIndices: [],
          skipped: true,
        });
      } catch (apiError) {
        console.warn("Failed to save skip vote to database:", apiError);
      }

      setHasVoted(true);
      toast.info("You skipped this poll.");

      if (onVoteSubmitted) {
        onVoteSubmitted(poll.id);
      }

      setTimeout(() => {
        handleClose();
      }, 600);
    } catch (error) {
      console.error("Error publishing poll skip event:", error);
      toast.error(
        error.message || "An error occurred while skipping this poll."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitVote = async () => {
    // Note: poll.type would be 'poll' (element type), so only check poll_type
    const pollType = poll?.poll_type || 'multiple_choice';
    const isOpenText = pollType === 'open_text';

    // Validate input based on poll type
    if (!poll || hasVoted) {
      return;
    }

    if (isOpenText) {
      if (!openTextResponse.trim()) {
        toast.error("Please enter your response.");
        return;
      }
    } else if (selectedOptions.length === 0) {
      toast.error("Please select at least one option.");
      return;
    }

    const participantId = mMeeting?.localParticipant?.id;
    const participantName =
      mMeeting?.localParticipant?.displayName || user?.name || meetingSettings?.yourName || "Guest";

    if (!participantId) {
      toast.error("Unable to submit vote. Participant ID not found.");
      return;
    }

    setIsSubmitting(true);

    const voteData = {
      sessionId: meetingSession || mMeeting?.meetingId,
      meetingId: mMeeting?.meetingId,
      pollId: poll.id,
      participantId,
      participantName: participantName,
      optionIndices: isOpenText ? [] : selectedOptions,
      textResponse: isOpenText ? openTextResponse.trim() : undefined,
      timestamp: Date.now(),
    };

    try {
      // First, publish via PubSub for real-time updates
      await publishPollVote({
        type: "vote_submitted",
        pollId: poll.id,
        vote: voteData,
      });

      // Then, save to database via API for persistence
      try {
        await pollServices.submitVote(voteData);
      } catch (apiError) {
        // Log and warn user - PubSub already delivered the vote for real-time display
        // but the vote may not be persisted for later analytics
        console.warn("Failed to save vote to database:", apiError);
        toast.warn("Vote recorded live but may not be saved permanently.");
      }

      setHasVoted(true);
      toast.success("Vote submitted successfully!");

      if (onVoteSubmitted) {
        onVoteSubmitted(poll.id);
      }

      if (!poll.settings?.shareResultsLive) {
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (error) {
      console.error("Error publishing POLL_VOTE event:", error);
      toast.error(
        error.message || "An error occurred while submitting your vote."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!poll || !isOpen) return null;

  // Get poll type and settings
  let pollType = poll.poll_type || 'multiple_choice';

  // Fallback detection for open_text polls created before poll_type was properly stored
  if (pollType === 'multiple_choice') {
    const options = poll.options || [];

    const hasPlaceholderOptions = options.length === 2 &&
      options.every(opt => {
        const text = typeof opt === 'string' ? opt : (opt?.text || '');
        return text === 'Response 1' || text === 'Response 2';
      });

    const hasEmptyTextOptions = options.length === 2 &&
      options.every(opt => {
        const text = typeof opt === 'string' ? opt : (opt?.text || '');
        return text === '' || text.trim() === '';
      });

    const hasNoOptions = options.length === 0;

    if (hasPlaceholderOptions || hasEmptyTextOptions || hasNoOptions) {
      pollType = 'open_text';
    }
  }
  const ratingSettings = poll.rating_settings || { scale_type: 'stars', max_value: 5 };
  const appearance = poll.appearance || {};
  const duration = poll.duration || {};
  const accentColor = appearance.accent_color || '#7700ff';
  const bgColor = appearance.background_color || '#1a1a2e';
  const textColor = appearance.text_color || '#ffffff';
  const showCountdown = duration.show_countdown !== false;
  const position = appearance.position || 'bottom_center';
  const size = appearance.size || 'medium';

  // Size classes for the panel width
  const sizeMaxWidth = {
    small: 'max-w-sm',
    medium: 'max-w-md',
    large: 'max-w-lg',
  };

  // Position → alignment classes for the wrapper
  const isFullscreen = position === 'fullscreen';
  const positionWrapperClass = {
    bottom_center: 'items-end justify-center pb-2 sm:pb-6',
    bottom_left: 'items-end justify-center sm:justify-start pb-2 sm:pb-6 sm:pl-6',
    bottom_right: 'items-end justify-center sm:justify-end pb-2 sm:pb-6 sm:pr-6',
    side_panel: 'items-end sm:items-start justify-center sm:justify-end sm:pt-6 sm:pr-6',
    fullscreen: 'items-center justify-center',
  }[position] || 'items-center justify-center';

  const panelSizeClass = isFullscreen
    ? 'max-w-4xl w-full min-h-[60vh]'
    : `${sizeMaxWidth[size] || 'max-w-md'} w-full`;

  // For rating polls, ensure correct max_value based on scale_type
  if (pollType === 'rating' && !ratingSettings.max_value) {
    ratingSettings.max_value = ratingSettings.scale_type === 'numbers' ? 10 : 5;
  }

  // Normalize options - handle both string array and object array
  const options = poll.options || [];
  const normalizedOptions = options.map((opt, idx) => {
    const derivedIndex =
      typeof opt === "object" && opt !== null && typeof opt.index === "number"
        ? opt.index
        : idx;

    if (typeof opt === "string") {
      return { text: opt, index: derivedIndex, value: idx + 1 };
    }
    // Handle object with text property
    if (opt && typeof opt === "object") {
      let text = '';
      if (typeof opt.text === 'string') {
        text = opt.text;
      } else if (typeof opt.label === 'string') {
        text = opt.label;
      } else if (opt.value !== undefined) {
        text = String(opt.value);
      } else {
        text = `Option ${idx + 1}`;
      }

      return {
        ...opt,
        text: text,
        index: derivedIndex,
        value: opt.value || idx + 1,
        color: opt.color,
      };
    }
    return { text: `Option ${idx + 1}`, index: derivedIndex, value: idx + 1 };
  });

  // Calculate vote percentages for results display
  const getOptionPercentage = (optionIndex) => {
    if (!voteResults || !voteResults.options) return 0;
    const option = voteResults.options.find((opt) => opt.index === optionIndex);
    if (!option || voteResults.totalVotes === 0) return 0;
    return Math.round((option.votes / voteResults.totalVotes) * 100);
  };

  const totalVotesCount =
    voteResults?.totalVotes ?? voteResults?.votes?.length ?? 0;
  const skippedCount =
    voteResults?.skippedCount ?? voteResults?.skipped?.length ?? 0;
  const totalResponses = totalVotesCount + skippedCount;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      {/* Backdrop - simple opacity, no blur to avoid iOS compositing issues */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-200 ${visible ? 'opacity-60' : 'opacity-0'}`}
        onClick={handleClose}
      />

      {/* Content wrapper */}
      <div className={`absolute inset-0 overflow-y-auto pointer-events-none`}>
        <div className={`flex min-h-full p-2 sm:p-4 text-center ${positionWrapperClass}`}>
          <div
            className={`${panelSizeClass} pointer-events-auto transform overflow-hidden rounded-xl border border-gray-600 p-4 sm:p-6 text-left align-middle shadow-xl max-h-[90vh] overflow-y-auto transition-all duration-200 ${visible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'}`}
            style={{ backgroundColor: bgColor, color: textColor }}
          >
            {/* Moderator Preview Badge */}
            {isModeratorView && (
              <div className="mb-3 px-3 py-1.5 bg-purple-600/20 border border-purple-500/30 rounded-lg">
                <p className="text-purple-300 text-xs font-medium text-center">
                  Moderator Preview - This is how participants see the poll
                </p>
              </div>
            )}

            {/* Countdown Timer */}
            {countdown !== null && showCountdown && !hasVoted && (
              <div className="mb-3 flex items-center justify-center gap-2">
                <div className="w-full bg-gray-700/50 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-1000 ease-linear"
                    style={{
                      width: `${(countdown / (poll.settings?.autoCloseSeconds || 60)) * 100}%`,
                      backgroundColor: accentColor,
                    }}
                  />
                </div>
                <span className="text-xs font-mono whitespace-nowrap" style={{ color: accentColor }}>
                  {countdown}s
                </span>
              </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-lg sm:text-xl font-bold" style={{ color: textColor }}>
                Poll
              </h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Question */}
            <div className="mb-4 sm:mb-6">
              <p className="text-white text-base sm:text-lg font-medium">
                {poll.question}
              </p>
              {poll.settings?.allowMultipleAnswers && (
                <p className="text-gray-400 text-sm mt-1">
                  Select all that apply
                </p>
              )}
              {pollType === 'rating' && (
                <p className="text-gray-400 text-sm mt-1">
                  {ratingSettings.min_label || 'Poor'} to {ratingSettings.max_label || 'Excellent'}
                </p>
              )}
            </div>

            {/* Options - Different rendering based on poll type */}
            {!hasVoted || !voteResults ? (
              <div className={`mb-4 sm:mb-6 ${pollType === 'rating' ? '' : 'space-y-2 sm:space-y-3'}`}>
                {/* Rating Poll - Stars or Numbers */}
                {pollType === 'rating' ? (
                  <div className="flex flex-col items-center w-full">
                    {/* Star/Number rating display */}
                    {(ratingSettings.scale_type || 'stars') === 'stars' ? (
                      /* Stars - single row */
                      <div className="flex justify-center gap-1">
                        {normalizedOptions.map((option, index) => {
                          const selectedValue = selectedOptions.length > 0 ? selectedOptions[0] + 1 : 0;
                          const isFilled = option.value <= selectedValue;
                          return (
                            <button
                              key={index}
                              onClick={() => setSelectedOptions([option.index])}
                              disabled={hasVoted || isSubmitting}
                              className={`p-1 transition-all ${hasVoted || isSubmitting ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:scale-110"}`}
                            >
                              <svg
                                className={`w-8 h-8 sm:w-10 sm:h-10 transition-colors ${isFilled ? 'text-yellow-400' : 'text-gray-600 hover:text-gray-400'}`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      /* Numbers - single row with small rounded buttons */
                      <div className="flex justify-center items-center gap-1">
                        {normalizedOptions.map((option, index) => {
                          const selectedValue = selectedOptions.length > 0 ? selectedOptions[0] + 1 : 0;
                          const isFilled = option.value <= selectedValue;
                          return (
                            <button
                              key={index}
                              onClick={() => setSelectedOptions([option.index])}
                              disabled={hasVoted || isSubmitting}
                              className={`w-8 h-8 flex items-center justify-center text-sm font-bold rounded-full border-2 transition-all ${
                                isFilled
                                  ? ''
                                  : 'text-gray-400 border-gray-600 hover:border-gray-400 hover:text-gray-300'
                              } ${hasVoted || isSubmitting ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:scale-110"}`}
                              style={isFilled ? {
                                color: 'white',
                                borderColor: accentColor,
                                backgroundColor: accentColor
                              } : {}}
                            >
                              {option.value}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {/* Rating labels */}
                    <div className="flex justify-between w-full mt-3 px-2">
                      <span className="text-gray-500 text-xs">{ratingSettings.min_label || 'Poor'}</span>
                      <span className="text-gray-500 text-xs">{ratingSettings.max_label || 'Excellent'}</span>
                    </div>
                    {/* Selected rating text */}
                    {selectedOptions.length > 0 && (
                      <p className="text-sm mt-3 font-medium" style={{ color: accentColor }}>
                        {selectedOptions[0] + 1} {(ratingSettings.scale_type || 'stars') === 'stars' ? (selectedOptions[0] === 0 ? 'Star' : 'Stars') : ''} selected
                      </p>
                    )}
                  </div>
                ) : pollType === 'yes_no' ? (
                  /* Yes/No Poll - Two big buttons */
                  normalizedOptions.map((option, index) => {
                    const isSelected = selectedOptions.includes(option.index);
                    const isYes = option.text.toLowerCase() === 'yes';
                    return (
                      <button
                        key={index}
                        onClick={() => handleOptionToggle(option.index)}
                        disabled={hasVoted || isSubmitting}
                        className={`w-full text-center px-4 sm:px-6 py-3 sm:py-4 rounded-lg border-2 transition-all ${
                          isSelected
                            ? isYes
                              ? "border-green-500 bg-green-500/20"
                              : "border-red-500 bg-red-500/20"
                            : "border-gray-600 bg-[#0f0f1e] hover:border-gray-500"
                        } ${hasVoted || isSubmitting ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <span className={`text-lg font-bold ${
                          isSelected
                            ? isYes ? 'text-green-400' : 'text-red-400'
                            : 'text-white'
                        }`}>
                          {option.text}
                        </span>
                      </button>
                    );
                  })
                ) : pollType === 'open_text' ? (
                  /* Open Text Poll - Textarea input */
                  <div className="w-full">
                    <textarea
                      value={openTextResponse}
                      onChange={(e) => setOpenTextResponse(e.target.value)}
                      disabled={hasVoted || isSubmitting}
                      placeholder="Type your response here..."
                      maxLength={poll.open_text_settings?.character_limit || 500}
                      className={`w-full h-24 sm:h-32 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border-2 bg-[#0f0f1e] text-white text-sm sm:text-base placeholder-gray-500 resize-none focus:outline-none transition-all ${
                        hasVoted || isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      style={{
                        borderColor: openTextResponse.trim() ? accentColor : '#4b5563',
                      }}
                    />
                    <div className="flex justify-end mt-2">
                      <span className="text-gray-400 text-xs">
                        {openTextResponse.length}/{poll.open_text_settings?.character_limit || 500}
                      </span>
                    </div>
                  </div>
                ) : (
                  /* Multiple Choice / Multiple Answer - Standard options */
                  normalizedOptions.map((option, index) => {
                    const isSelected = selectedOptions.includes(option.index);
                    return (
                      <button
                        key={index}
                        onClick={() => handleOptionToggle(option.index)}
                        disabled={hasVoted || isSubmitting}
                        className={`w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border-2 transition-all ${
                          !isSelected ? "border-gray-600 bg-[#0f0f1e] hover:border-gray-500" : ""
                        } ${hasVoted || isSubmitting ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        style={isSelected ? {
                          borderColor: accentColor,
                          backgroundColor: `${accentColor}33`
                        } : {}}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {option.color && (
                              <span
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: option.color }}
                              />
                            )}
                            <span className="text-white text-sm">{option.text}</span>
                          </div>
                          {isSelected && (
                            <svg
                              className="w-5 h-5"
                              style={{ color: accentColor }}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            ) : (
              /* Results Display */
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                {pollType === 'open_text' ? (
                  /* Open Text Results - Show submitted response */
                  <div className="w-full px-4 py-3 rounded-lg border-2"
                    style={{
                      borderColor: accentColor,
                      backgroundColor: `${accentColor}33`
                    }}
                  >
                    <p className="text-gray-400 text-xs mb-2">Your response:</p>
                    <p className="text-white text-sm">{openTextResponse}</p>
                  </div>
                ) : (
                  normalizedOptions.map((option, index) => {
                    const percentage = getOptionPercentage(option.index);
                    const isSelectedOption = selectedOptions.includes(
                      option.index
                    );
                    return (
                      <div
                        key={index}
                        className={`w-full px-4 py-3 rounded-lg border-2 ${
                          !isSelectedOption ? "border-gray-600 bg-[#0f0f1e]" : ""
                        }`}
                        style={isSelectedOption ? {
                          borderColor: accentColor,
                          backgroundColor: `${accentColor}33`
                        } : {}}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white text-sm">
                            {option.text}
                            {isSelectedOption && (
                              <span className="ml-2 text-xs" style={{ color: accentColor }}>
                                (Your vote)
                              </span>
                            )}
                          </span>
                          <span className="text-gray-400 text-sm">
                            {percentage}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all"
                            style={{ width: `${percentage}%`, backgroundColor: accentColor }}
                          />
                        </div>
                        {voteResults.options?.[index]?.votes !==
                          undefined && (
                          <p className="text-gray-400 text-xs mt-1">
                            {voteResults.options[index].votes} vote
                            {voteResults.options[index].votes !== 1
                              ? "s"
                              : ""}
                          </p>
                        )}
                      </div>
                    );
                  })
                )}
                {totalVotesCount !== undefined && (
                  <p className="text-gray-400 text-sm text-center mt-4">
                    {pollType === 'open_text' ? 'Responses' : 'Votes'}: {totalVotesCount}
                  </p>
                )}
                {totalResponses && pollType !== 'open_text' ? (
                  <p className="text-gray-400 text-xs text-center">
                    Responses: {totalResponses}
                  </p>
                ) : null}
                {skippedCount ? (
                  <p className="text-gray-400 text-xs text-center">
                    Skipped: {skippedCount}
                  </p>
                ) : null}
              </div>
            )}

            {/* Action Buttons */}
            {isModeratorView ? (
              /* Moderator view - Close Preview and End Poll buttons */
              <div className="flex gap-2 sm:gap-3 justify-center">
                <button
                  onClick={handleClose}
                  className="px-4 sm:px-6 py-2 rounded-lg text-white text-sm sm:text-base transition-colors bg-gray-600 hover:bg-gray-700"
                >
                  Close Preview
                </button>
                <button
                  onClick={onEndPoll}
                  className="px-4 sm:px-6 py-2 rounded-lg text-white text-sm sm:text-base transition-colors bg-red-600 hover:bg-red-700"
                >
                  End Poll
                </button>
              </div>
            ) : (
              /* Participant view - Vote buttons */
              <>
                {!hasVoted && (
                  <div className="flex gap-2 sm:gap-3">
                    {!poll.settings?.autoCloseAfter && (
                      <button
                        onClick={handleSkipVote}
                        className="flex-1 px-3 sm:px-4 py-2 rounded-lg bg-transparent border border-gray-600 text-white text-sm sm:text-base hover:bg-gray-700 transition-colors"
                        disabled={isSubmitting}
                      >
                        Skip
                      </button>
                    )}
                    <button
                      onClick={handleSubmitVote}
                      disabled={(pollType === 'open_text' ? !openTextResponse.trim() : selectedOptions.length === 0) || isSubmitting}
                      className="flex-1 px-3 sm:px-4 py-2 rounded-lg text-white text-sm sm:text-base transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: accentColor }}
                    >
                      {isSubmitting ? "Submitting..." : (pollType === 'open_text' ? "Submit Response" : "Submit Vote")}
                    </button>
                  </div>
                )}

                {hasVoted &&
                  voteResults &&
                  !poll.settings?.shareResultsLive && (
                    <div className="flex justify-center">
                      <button
                        onClick={handleClose}
                        className="px-6 py-2 rounded-lg text-white transition-colors"
                        style={{ backgroundColor: accentColor }}
                      >
                        Close
                      </button>
                    </div>
                  )}

                {/* Auto-close countdown */}
                {countdown !== null && showCountdown && !hasVoted && (
                  <p className="text-gray-400 text-xs text-center mt-4">
                    Poll closes in {countdown}s
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PollVotingModal;
